import {getElementAbsoluteYPosition} from '../../_Global/js/utils';
import {gsap} from '../../_Global/js/global';

(() => {
    const throttle = require('lodash/throttle');
    const debounce = require('lodash/debounce');
    const ally = require('ally.js');

    const componentNodeList = document.querySelectorAll('[data-component="on-this-page"]');
    const pageContent = document.querySelector('[data-role="otp-content"]');
    const menuItemActiveClass = 'side-tab__button--active';

    /**
     * The freqency (in ms) to run the scroll position checks.
     */
    const scrollUpdateFrequency = 250;

    /**
     * Vertical distance in pixels that a user must scroll
     * back up the page before hiding the sticky menu.
     * This is applied in a single scroll event, not cumulative.
     */
    const stickyScrollUpDistance = 50;

    /**
     * The number of pixels from the current scroll position
     * where are heading will be considered "active".
     * Think of this as an invisible box at the top of the page,
     * if the heading is in that box area, it is considered "active"
     */
    const currentHeaderRange = 150;

    let headingNodeList = [];
    let menuListElement = null;
    let lastScrollPosition = 0;
    let previousScrollEndPosition = 0;
    let dialogMenuActive = false;
    let stickySectionActive = false;
    let disabledHandle;
    let tabHandle;
    let keyHandle;

    if (pageContent) {
        headingNodeList = pageContent.querySelectorAll('h2');
    }

    /**
     * click event
     * this event scrolls a heading item into view
     *
     * @param {Event} e
     * the click event
     *
     * @param {Element} listElement
     * the list containing the navigation items
     */
    function scrollTo(listElement, e) {
        e.preventDefault();

        // Remove any existing active style
        listElement.querySelector(`.${menuItemActiveClass}`)?.classList.remove(menuItemActiveClass);

        // Animate to the heading position
        gsap.to(window, {
            duration: 1,
            scrollTo: {
                y: e.target.getAttribute('href'),
                offsetY: 100,
            },
        });
    }

    /**
     * creates a list of links for the component
     *
     * @param {NodeList} headings
     * the headings to use when generating the list
     *
     * @param {Element} listElement
     * the list containing the navigation items
     *
     * @return {array}
     */
    function createNavigationItems(headings, listElement) {
        const navigationItems = [];

        [...headings].forEach((heading, index) => {
            // Create the list item
            const listItem = document.createElement('li');

            listItem.classList.add('side-tab__list-item');

            // Create the link for the list item
            const link = document.createElement('a');

            heading.id = `otp-heading-${index}`;

            link.href = `#${heading.id}`;
            link.classList.add('side-tab__list-link', 'side-tab__button', 'body-default');
            link.textContent = heading.textContent;
            link.addEventListener('click', scrollTo.bind(this, listElement));

            listItem.appendChild(link);

            navigationItems.push(listItem);
        });

        return navigationItems;
    }

    /**
     * respond to clicks on the open dialog button
     *
     * @param {Event} e
     * the click event
     *
     * @param {Element} dialogElement
     * the dialog to open
     */
    function onDialogButtonClick(e, dialogElement) {
        e.preventDefault();

        toggleDialogNavigation(!dialogMenuActive, dialogElement);
    }

    /**
     * Simple event disabler
     * Mostly to have a reference to an event function when toggling scroll functionality on touch devices
     * @param {Event} e
     */
    function preventDefault(e) {
        componentNodeList.forEach((component) => {
            if (!component.contains(e.target) || !e.target.classList.contains('side-tab__list-link', 'side-tab__list')) {
                e.preventDefault();
            }
        });
    }

    /**
     * open or close the navigation menu
     *
     * @param {Boolean} activate
     * whether to open or close the menu
     *
     * @param {Element} dialogElement
     * the dialog to open
     */
    function toggleDialogNavigation(activate, dialogElement) {
        if (activate) {
            // Enable the menu
            // Add the active CSS class for the modal
            dialogElement.classList.add('on-this-page-modal--active');

            // Remove the aria-hidden attribute for the modal
            dialogElement.setAttribute('aria-hidden', 'false');

            // Disable user scrolling of the page
            document.body.classList.add('overflow-y-hidden');
            // For touch devices, prevent touchmove
            document.body.addEventListener('touchmove', preventDefault, {passive: false});

            // Check if we're likely PC browsing
            if (navigator.maxTouchPoints <= 1) {
                /**
                 * Make sure that no element outside of the dialog
                 * can be interacted with while the dialog is visible.
                 * This means we don't have to handle Tab and Shift+Tab,
                 * but can defer that to the browser's internal handling.
                 *
                 * @source https://allyjs.io/tutorials/accessible-dialog.html
                 */
                disabledHandle = ally.maintain.disabled({
                    filter: dialogElement,
                });

                /**
                 * Make sure that Tab key controlled focus is trapped within
                 * the tabsequence of the dialog and does not reach the
                 * browser's UI, e.g. the location bar.
                 *
                 * @source https://allyjs.io/tutorials/accessible-dialog.html
                 */
                tabHandle = ally.maintain.tabFocus({
                    context: dialogElement,
                });

                // React to escape keys as mandated by ARIA Practices
                keyHandle = ally.when.key({
                    escape: (evt) => {
                        evt.stopPropagation();
                        toggleDialogNavigation(false, dialogElement);
                    },
                });
            }

            // Save that the dialog state
            dialogMenuActive = true;
        } else {
            // Disable the menu
            // Remove the active CSS class for the modal
            dialogElement.classList.remove('on-this-page-modal--active');

            // Add the aria-hidden attribute for the modal
            dialogElement.setAttribute('aria-hidden', 'true');

            // Enable user scrolling of the page
            document.body.classList.remove('overflow-y-hidden');
            // For touch devices, enable touchmove
            document.body.removeEventListener('touchmove', preventDefault);

            // Check if we're likely PC browsing
            if (navigator.maxTouchPoints <= 1) {
                // Undo disabling elements outside of the dialog
                disabledHandle.disengage();

                // Undo trapping Tab key focus
                tabHandle.disengage();

                // Undo listening to keyboard Escape
                keyHandle.disengage();

                // Save that the dialog state
            }

            dialogMenuActive = false;
        }
    }

    /**
     * respond to the page scrolling event
     */
    function onPageScroll() {
        // Selector for targeting the element that should be sticky
        const stickySectionQuerySelector = '[data-behaviour="otp-sticky-section"]';
        const stickyParentQuerySelector = '[data-role="otp-sticky-parent"]';

        // CSS class to apply to content to make it sticky
        const stickySectionClassName = 'is-sticky-section';

        // Capture the most recent scroll position
        const recentScrollPosition = window.scrollY;

        // Capture the current position of the non-sticky parent element
        // This may change at different viewport widths
        const currentNonStickySectionPosition = parseInt(getElementAbsoluteYPosition(stickyParentQuerySelector), 10);

        if (recentScrollPosition > lastScrollPosition) {
            // User is scrolling down

            // Check if user has scrolled past the "default" (when it's not sticky) sticky section position
            if (!stickySectionActive && recentScrollPosition > currentNonStickySectionPosition) {
                // Add the sticky functionality
                document.querySelector(stickySectionQuerySelector).classList.add(stickySectionClassName);

                stickySectionActive = true;
            }
        } else {
            // User is scrolling up

            // Check if user has scrolled past the "default" (when it's not sticky) sticky section position
            if (stickySectionActive && recentScrollPosition <= currentNonStickySectionPosition) {
                // Remove the sticky functionality
                document.querySelector(stickySectionQuerySelector).classList.remove(stickySectionClassName);

                stickySectionActive = false;
            }

            // Check if user has scrolled up a minimum threshold in the current scroll action
            if (recentScrollPosition < previousScrollEndPosition - stickyScrollUpDistance) {
                // Remove the sticky functionality
                document.querySelector(stickySectionQuerySelector).classList.remove(stickySectionClassName);

                stickySectionActive = false;
            }
        }

        // Store the most recent scroll position
        lastScrollPosition = recentScrollPosition;

        // Update the menu styles to track scroll progress
        setActiveHeadingStyle();
    }

    /**
     * capture the scroll position of the page when
     * the user has finished scrolling
     */
    function onPageScrollEnd() {
        previousScrollEndPosition = window.scrollY;

        // Trigger a final check for the scroll layout
        onPageScroll();
    }

    /**
     * update the navigation list to highlight
     * the current active heading link
     */
    function setActiveHeadingStyle() {
        // Capture the most recent scroll position
        const recentScrollPosition = window.scrollY;

        const headerActiveArea = recentScrollPosition + currentHeaderRange;

        let previousHeadingId = '';
        let watchZoneHeadingId = '';

        // This will be updated to be the active heading. Default is to use the first heading.
        let activeHeadingId = 'otp-heading-0';

        headingNodeList.forEach((heading) => {
            const headingPosition = parseInt(getElementAbsoluteYPosition(`#${heading.id}`), 10);

            if (headingPosition < recentScrollPosition) {
                // The user has scrolled past this heading
                previousHeadingId = heading.id;
            }

            if (headingPosition > recentScrollPosition && headingPosition <= headerActiveArea) {
                // This heading is near the top of the page
                watchZoneHeadingId = heading.id;
            }
        });

        // Remove any existing active style
        menuListElement.querySelector(`.${menuItemActiveClass}`)?.classList.remove(menuItemActiveClass);

        if (previousHeadingId !== '') {
            activeHeadingId = previousHeadingId;
        }

        // The order is important here, the "watch zone" supersedes the previous heading
        if (watchZoneHeadingId !== '') {
            activeHeadingId = watchZoneHeadingId;
        }

        // Add the active class to the list item
        document.querySelector(`[href="#${activeHeadingId}"]`)?.classList.add(menuItemActiveClass);
    }

    if (!headingNodeList.length) {
        componentNodeList.forEach((node) => node.remove());
    }

    /**
     * Check if the component and relevant headings
     * exist on the page.
     *
     * Note: We assume only one instance of this
     * component has been added to the page.
     */
    if (componentNodeList.length && headingNodeList.length) {
        const componentNode = componentNodeList[0];

        menuListElement = componentNode.querySelector('[data-role="otp-link-list"]');

        const links = createNavigationItems(headingNodeList, menuListElement);
        const dialogContentElement = componentNode.querySelector('[role="dialog"]');
        const dialogButtonNodeList = componentNode.querySelectorAll('[data-role="otp-dialog-button"]');
        const mql = window.matchMedia('(min-width: 1200px)');

        if (dialogContentElement && dialogButtonNodeList.length) {
            // Add click listeners to each button (i.e. open and close)
            [...dialogButtonNodeList].forEach((button) => {
                button.addEventListener('click', (e) => {
                    onDialogButtonClick(e, dialogContentElement);
                });
            });
        }

        // Listen for the scrolling event for the sticky buttons
        document.addEventListener(
            'scroll',
            throttle(onPageScroll, scrollUpdateFrequency, {
                leading: true,
                trailing: false,
            }),
        );

        // Catch the scroll end event for the sticky buttons
        document.addEventListener(
            'scroll',
            debounce(onPageScrollEnd, scrollUpdateFrequency, {
                leading: false,
                trailing: true,
            }),
        );

        // Listen for the media switch to 1200px
        mql.addEventListener('change', (mqlEvent) => {
            // Reset the dialog menu if it's open and the page width has changed
            if (mqlEvent.matches && dialogMenuActive) {
                toggleDialogNavigation(false, dialogContentElement);
            }
            // Remove the aria-hidden value from the modal
            dialogContentElement.setAttribute('aria-hidden', 'false');
        });

        // Add the navigation items to the menu
        menuListElement.append(...links);
    }
})();
